import React, { useState, useRef, useEffect } from 'react';
import { Settings, ChevronUp, ChevronDown, RotateCcw, GripVertical } from 'lucide-react';
import type { ExpandedColumnDef } from './columnRegistry';

/**
 * Sub-column presets for the expanded month group — the three jobs people
 * actually open a month for. The month's own (plan) cell always leads the
 * group and is not a pickable column. Picking a preset replaces the visible
 * set; toggles afterwards make it a custom set again. Persisted with the
 * columns.
 */
export const COLUMN_PRESETS: Array<{ id: string; label: string; keys: string[] }> = [
  { id: 'variance', label: 'Variance', keys: ['actual', 'var', 'vsLy'] },
  { id: 'budget', label: 'Budget pack', keys: ['actual', 'budget', 'vsBud', 'ly'] },
  { id: 'por', label: 'Per-room', keys: ['actual', 'por', 'pctRev'] },
];

/** Which preset the current visible set IS, if any (order-insensitive). */
export function matchPreset(visibleKeys: string[], allowed: Set<string>): string | null {
  const have = visibleKeys.filter((k) => allowed.has(k));
  for (const p of COLUMN_PRESETS) {
    const want = p.keys.filter((k) => allowed.has(k));
    if (want.length === have.length && want.every((k) => have.includes(k))) return p.id;
  }
  return null;
}

const KIND_LABEL: Record<string, string> = { data: 'DATA', computed: 'VAR' };
function kindOf(col: ExpandedColumnDef): string {
  if (/pct|%/i.test(col.key) || /%/.test(col.label)) return 'PCT';
  return KIND_LABEL[col.category] || '';
}

interface ExpandedColumnSettingsProps {
  allColumns: ExpandedColumnDef[];
  visibleKeys: string[];
  onToggle: (key: string) => void;
  onMove: (key: string, direction: 'up' | 'down') => void;
  /** Replace the whole visible sequence (drag-and-drop reorder, presets). */
  onReorder?: (nextKeys: string[]) => void;
  onReset: () => void;
  /** Controlled open state (the ribbon's ⋯ menu opens the picker). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Render the popover only — the host supplies its own trigger. */
  hideTrigger?: boolean;
}

export function ExpandedColumnSettings({
  allColumns,
  visibleKeys,
  onToggle,
  onMove,
  onReorder,
  onReset,
  open,
  onOpenChange,
  hideTrigger = false,
}: ExpandedColumnSettingsProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setIsOpen = (next: boolean) => {
    if (onOpenChange) onOpenChange(next);
    if (open === undefined) setInternalOpen(next);
  };
  // drag-and-drop reorder (same interaction as the reports Columns picker)
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<{ key: string; after: boolean } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        !(buttonRef.current && buttonRef.current.contains(e.target as Node))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build ordered list: visible columns first (in order), then hidden columns
  const orderedColumns = [
    ...visibleKeys.map(key => allColumns.find(c => c.key === key)!).filter(Boolean),
    ...allColumns.filter(c => !visibleKeys.includes(c.key)),
  ];
  const allowed = new Set(allColumns.map((c) => c.key));
  const activePreset = matchPreset(visibleKeys, allowed);

  const clearDrag = () => {
    setDragKey(null);
    setDragOver(null);
  };

  return (
    <div className={hideTrigger ? 'contents' : 'relative'}>
      {!hideTrigger && (
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className="p-1.5 rounded hover:bg-slate-200 transition-colors"
          style={{ color: isOpen ? 'var(--lodge-orange-500)' : 'var(--lodge-onyx-400)' }}
          title="Configure expanded columns"
        >
          <Settings size={14} />
        </button>
      )}

      {isOpen && (
        <div
          ref={popoverRef}
          className="fv3-colpicker absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-slate-200 z-[9999] w-[296px] overflow-hidden"
        >
          <div className="px-3.5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-[13px] font-semibold" style={{ color: 'var(--lodge-onyx-900)' }}>Expanded columns</div>
              <div className="text-[11.5px]" style={{ color: 'var(--lodge-onyx-400)' }}>
                {visibleKeys.length} of {allColumns.length} shown · saved per user
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onReset}
                className="text-[11px] flex items-center gap-1 transition-colors"
                style={{ color: 'var(--lodge-onyx-400)' }}
                title="Back to the default columns"
              >
                <RotateCcw size={10} />
                Reset
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="text-[12.5px] font-medium"
                style={{ color: 'var(--lodge-onyx-500)' }}
              >
                Done
              </button>
            </div>
          </div>

          {onReorder && (
            <div className="px-3.5 py-2.5 border-b border-slate-100 flex flex-col gap-1.5">
              <div className="text-[10.5px] font-semibold" style={{ letterSpacing: '0.7px', color: 'var(--lodge-onyx-400)' }}>PRESET</div>
              <div className="flex gap-1.5 flex-wrap">
                {COLUMN_PRESETS.map((p) => {
                  const active = activePreset === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => onReorder(p.keys.filter((k) => allowed.has(k)))}
                      className="text-xs font-medium px-2.5 py-1 rounded-full border transition-colors"
                      style={active
                        ? { background: 'var(--lodge-onyx-900)', color: 'var(--lodge-white)', borderColor: 'var(--lodge-onyx-900)' }
                        : { background: 'var(--lodge-white)', color: 'var(--lodge-onyx-600)', borderColor: 'var(--lodge-onyx-200)' }}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="py-1 max-h-[264px] overflow-y-auto">
            {orderedColumns.map((col) => {
              const isVisible = visibleKeys.includes(col.key);
              const visibleIndex = visibleKeys.indexOf(col.key);
              const isFirst = visibleIndex === 0;
              const isLast = visibleIndex === visibleKeys.length - 1;
              const draggable = isVisible && Boolean(onReorder);
              const isDragSource = dragKey === col.key;
              const dropHere = dragOver?.key === col.key && dragKey && dragKey !== col.key;

              return (
                <div
                  key={col.key}
                  className={`flex items-center gap-2.5 px-3.5 py-[7px] hover:bg-slate-50 ${
                    draggable ? 'cursor-grab active:cursor-grabbing' : ''
                  } ${isDragSource ? 'opacity-40' : ''}`}
                  style={
                    dropHere
                      ? {
                          boxShadow: dragOver!.after
                            ? 'inset 0 -2px 0 var(--lodge-orange-400)'
                            : 'inset 0 2px 0 var(--lodge-orange-400)',
                        }
                      : undefined
                  }
                  draggable={draggable}
                  onDragStart={draggable ? (e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    try { e.dataTransfer.setData('text/plain', col.key); } catch { /* IE */ }
                    setDragKey(col.key);
                  } : undefined}
                  onDragOver={draggable ? (e) => {
                    if (!dragKey || dragKey === col.key) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    const rect = e.currentTarget.getBoundingClientRect();
                    const after = e.clientY > rect.top + rect.height / 2;
                    setDragOver((cur) =>
                      cur?.key === col.key && cur.after === after ? cur : { key: col.key, after }
                    );
                  } : undefined}
                  onDrop={draggable ? (e) => {
                    e.preventDefault();
                    if (dragKey && dragKey !== col.key && onReorder) {
                      const next = visibleKeys.filter((k) => k !== dragKey);
                      const idx = next.indexOf(col.key) + (dragOver?.after ? 1 : 0);
                      next.splice(idx, 0, dragKey);
                      onReorder(next);
                    }
                    clearDrag();
                  } : undefined}
                  onDragEnd={clearDrag}
                >
                  {/* Drag handle — visible columns only; spacer keeps alignment */}
                  <GripVertical
                    size={12}
                    className={`flex-shrink-0 ${draggable ? 'text-slate-300' : 'invisible'}`}
                    aria-hidden="true"
                  />

                  {/* Toggle */}
                  <button
                    onClick={() => onToggle(col.key)}
                    className="w-[30px] h-4 rounded-full relative transition-colors flex-shrink-0"
                    style={{ background: isVisible ? 'var(--lodge-orange-400)' : 'var(--lodge-onyx-200)' }}
                    aria-pressed={isVisible}
                    aria-label={`${isVisible ? 'Hide' : 'Show'} ${col.label}`}
                  >
                    <div
                      className="w-3 h-3 rounded-full bg-white absolute top-0.5 transition-transform shadow-sm"
                      style={{ transform: `translateX(${isVisible ? 16 : 2}px)` }}
                    />
                  </button>

                  {/* Label */}
                  <span className="text-[12.5px] flex-1" style={{ color: isVisible ? 'var(--lodge-onyx-800)' : 'var(--lodge-onyx-400)' }}>
                    {col.label}
                  </span>

                  <span className="text-[10px] font-semibold" style={{ letterSpacing: '0.5px', color: 'var(--lodge-onyx-300)' }}>{kindOf(col)}</span>

                  {/* Reorder buttons - only for visible columns */}
                  {isVisible && (
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => onMove(col.key, 'up')}
                        disabled={isFirst}
                        className={`p-0.5 rounded transition-colors ${
                          isFirst ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                        }`}
                        aria-label={`Move ${col.label} up`}
                      >
                        <ChevronUp size={12} />
                      </button>
                      <button
                        onClick={() => onMove(col.key, 'down')}
                        disabled={isLast}
                        className={`p-0.5 rounded transition-colors ${
                          isLast ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                        }`}
                        aria-label={`Move ${col.label} down`}
                      >
                        <ChevronDown size={12} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
